/**
 * SFE Dashboard cascading-filter hook (Requirements 8.1-8.7, 8.9).
 *
 * `state/filterCascade.js` holds the pure model — cascade order, per-level
 * dependencies, serialization, and the reducer that clears descendants and
 * invalidates their in-flight requests. This hook is the only place that turns that
 * model into network activity: it decides *when* a level needs new options, owns one
 * `AbortController` per level, and tags every request with the level's current
 * `requestId` so a superseded response cannot repopulate a filter the user has
 * already moved past.
 *
 * It replaces the seven `useEffect` cascade steps in `SfeDashboard.jsx`, which each
 * re-derived their own parent parameters and had drifted apart (the states effect read
 * `selFy`/`selMo` while depending only on `selDiv`, so a fiscal-year change could
 * leave stale states on screen) and which cancelled nothing, so a slow response for a
 * discarded parent could still land.
 *
 * Two rules that are easy to get wrong, stated once here:
 *
 * - **Fetching only.** Clearing descendants on selection (Req 8.9) belongs to the
 *   reducer's `SELECT`. This hook never clears anything; it reacts to what the
 *   reducer has already done.
 * - **Cancellation is not a failure.** An `AbortError` raised because we aborted the
 *   previous request is swallowed and never reaches `errors`.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import {
  CASCADE_ORDER,
  PARAM_NAMES,
  cascadeReducer,
  descendantsOf,
  initialCascadeState,
  requestParamsFor,
  serializeMultiSelect,
} from "../state/filterCascade";
import {
  fetchBdmList,
  fetchBrands,
  fetchDivisions,
  fetchFiscalYears,
  fetchMonths,
  fetchStates,
  fetchZmList,
} from "../api/sfeApi";
import { formatErrorMessage, normalizeApiError } from "../../../utils/apiErrors";

/**
 * Cascade level -> options fetcher, called as `fetcher(params, { signal })`.
 *
 * `params` is whatever `requestParamsFor(level, selections)` produced, already keyed
 * by backend parameter name, so the positional fetcher signatures in `api/sfeApi.js`
 * are spelled out exactly once — here — instead of in seven effects (Req 8.2-8.7).
 *
 * @type {Readonly<Record<string, (params: Record<string, string>, options: { signal: AbortSignal }) => Promise<unknown>>>}
 */
const FETCHERS = Object.freeze({
  fiscalYear: (params, options) => fetchFiscalYears(options),
  months: (params, options) => fetchMonths(params.fiscal_year, options),
  divisions: (params, options) => fetchDivisions(params.fiscal_year, params.yearmonth, options),
  states: (params, options) =>
    fetchStates(params.fiscal_year, params.yearmonth, params.division, options),
  brands: (params, options) =>
    fetchBrands(params.fiscal_year, params.yearmonth, params.division, params.state, options),
  bdm: (params, options) =>
    fetchBdmList(params.yearmonth, params.division, params.state, params.brand, options),
  zm: (params, options) =>
    fetchZmList(params.yearmonth, params.division, params.state, params.brand, params.bdm, options),
});

/**
 * Ancestors that must have a selection before a level's request is worth sending.
 *
 * This is a *subset* of `DEPENDENCIES`, not a restatement of it. A declared ancestor
 * with no selection is sent as the empty string and read by the backend as
 * "unfiltered", so most of them are optional; these are the ones the endpoint refuses
 * to answer without, mirroring the guards in `api/sfeApi.js`. That is why `bdm` and
 * `zm` list only `months` and `divisions` even though they are also filtered by
 * states and brands: those two lists load as soon as a division is chosen and narrow
 * further if the user goes on to pick states or brands.
 *
 * `fiscalYear` requires nothing, which is what makes it load on mount (Req 8.1).
 *
 * @type {Readonly<Record<string, readonly string[]>>}
 */
const REQUIRED_SELECTIONS = Object.freeze({
  fiscalYear: Object.freeze([]),
  months: Object.freeze(["fiscalYear"]),
  divisions: Object.freeze(["fiscalYear", "months"]),
  states: Object.freeze(["fiscalYear", "months", "divisions"]),
  brands: Object.freeze(["fiscalYear", "months", "divisions", "states"]),
  bdm: Object.freeze(["months", "divisions"]),
  zm: Object.freeze(["months", "divisions"]),
});

/**
 * Levels whose selections the data panels need before any of them can load.
 *
 * The same three filters the dashboard's `canFetch` guard has always required.
 *
 * @type {readonly string[]}
 */
const DATA_PANEL_REQUIRED = Object.freeze(["fiscalYear", "months", "divisions"]);

/** True when every level in `required` has at least one value selected. */
function hasSelections(selections, required) {
  return required.every((level) => {
    const values = selections[level];
    return Array.isArray(values) && values.length > 0;
  });
}

/**
 * True for a rejection caused by our own cancellation.
 *
 * Matches the classifier in `utils/axiosConfig.jsx`: Axios raises `CanceledError` /
 * `ERR_CANCELED` for an aborted signal, while a bare `AbortController` produces a DOM
 * `AbortError`. Aborting the previous request for a level is intentional, so none of
 * these may surface as a user-facing error.
 */
function isAbortError(error) {
  if (error === null || typeof error !== "object") return false;
  return (
    error.name === "AbortError" ||
    error.name === "CanceledError" ||
    error.code === "ERR_CANCELED"
  );
}

/**
 * Display string for a failed level.
 *
 * The shared Axios pipeline attaches `error.apiError` (an `ApiErrorDescriptor`) to
 * every rejection, so the usual path is a plain format. `normalizeApiError` covers
 * anything thrown outside that pipeline, which keeps the filter bar's error text
 * identical to the rest of the feature's (Req 10.1, 10.3).
 */
function describeError(error) {
  const descriptor =
    error !== null && typeof error === "object" && error.apiError
      ? error.apiError
      : normalizeApiError(error);
  return formatErrorMessage(descriptor);
}

/** Builds one mutable record with the same value for every cascade level. */
function levelRecord(makeValue) {
  return CASCADE_ORDER.reduce((record, level) => {
    record[level] = makeValue(level);
    return record;
  }, {});
}

/**
 * Identifies one logical request for a level.
 *
 * Both halves matter. The parameters change when an ancestor's selection changes, and
 * the `requestId` changes whenever the reducer invalidates the level — including when
 * the user re-selects the *same* ancestor values, which clears the level's options
 * without changing a single parameter. Keying on the parameters alone would leave that
 * level permanently empty.
 */
function fetchKeyFor(requestId, params) {
  return `${requestId}|${JSON.stringify(params)}`;
}

/**
 * Drives the SFE filter cascade: options fetching, cancellation, and error state.
 *
 * Behaviour:
 * - Fiscal years load on mount (Req 8.1).
 * - Selecting a level re-fetches every downstream level that has become fetchable,
 *   each with exactly its declared ancestor parameters (Req 8.2-8.7). The reducer has
 *   already cleared those levels (Req 8.9); this hook only refills them.
 * - Each level has one `AbortController`. Starting a new request for a level aborts
 *   the previous one, and selecting a level aborts every descendant's in-flight
 *   request, since the reducer has just made their responses stale.
 * - Everything in flight is aborted on unmount.
 *
 * @returns {{
 *   selections: Record<string, string[]>,
 *   options: Record<string, { id: string, label: string }[]>,
 *   loading: Record<string, boolean>,
 *   errors: Record<string, string|null>,
 *   params: Record<string, string>,
 *   paramsKey: string,
 *   canFetch: boolean,
 *   select: (level: string, values: string[]) => void,
 *   reset: () => void,
 *   refresh: (level: string) => void,
 * }}
 */
export function useSfeFilters() {
  const [state, dispatch] = useReducer(cascadeReducer, initialCascadeState);

  /**
   * Mirror of the reducer state, assigned during render so callbacks and async
   * continuations read the current `requestId` rather than one captured by a closure.
   * The reducer drops any action whose `requestId` no longer matches, so a stale read
   * here would silently discard a live response.
   */
  const stateRef = useRef(state);
  stateRef.current = state;

  /** One `AbortController` per level; null when nothing is in flight. */
  const controllersRef = useRef(levelRecord(() => null));

  /** The last request key started per level, so the driving effect stays idempotent. */
  const fetchKeysRef = useRef(levelRecord(() => null));

  /** False after unmount, so a late continuation does not dispatch into a dead store. */
  const mountedRef = useRef(true);

  /** Aborts and forgets a level's in-flight request, if any. */
  const abortLevel = useCallback((level) => {
    const controller = controllersRef.current[level];
    if (controller === null) return;
    controllersRef.current[level] = null;
    controller.abort();
  }, []);

  /**
   * Starts a fetch for one level, cancelling whatever it had in flight first.
   *
   * The request is tagged with the level's current `requestId`, which is what makes
   * `OPTIONS_LOADING` / `OPTIONS_LOADED` / `OPTIONS_FAILED` self-invalidating: if the
   * user changes an ancestor while this request is open, the reducer has bumped the id
   * and every one of these dispatches becomes a no-op.
   */
  const startFetch = useCallback(
    (level, params) => {
      abortLevel(level);

      const controller = new AbortController();
      controllersRef.current[level] = controller;

      const requestId = stateRef.current.requestId[level];
      dispatch({ type: "OPTIONS_LOADING", level, requestId });

      FETCHERS[level](params, { signal: controller.signal })
        .then((payload) => {
          if (!mountedRef.current || controller.signal.aborted) return;
          dispatch({ type: "OPTIONS_LOADED", level, requestId, options: payload });
        })
        .catch((error) => {
          // An abort is a cancellation we asked for, not a failure to report.
          if (!mountedRef.current || controller.signal.aborted || isAbortError(error)) return;
          dispatch({ type: "OPTIONS_FAILED", level, requestId, error: describeError(error) });
        })
        .finally(() => {
          if (controllersRef.current[level] === controller) {
            controllersRef.current[level] = null;
          }
        });
    },
    [abortLevel],
  );

  /**
   * The single cascade driver: every level that is fetchable and whose request key has
   * moved on gets a request, in cascade order.
   *
   * Keyed rather than dependency-listed, which is what removes the class of bug the
   * seven effects had: the key is derived from the same `selections` the request is
   * built from, so a parameter can never change without the request following it.
   */
  useEffect(() => {
    CASCADE_ORDER.forEach((level) => {
      if (!hasSelections(state.selections, REQUIRED_SELECTIONS[level])) {
        // Not fetchable: the reducer has emptied this level, and anything still open
        // for it describes a selection the user has abandoned.
        fetchKeysRef.current[level] = null;
        abortLevel(level);
        return;
      }

      const params = requestParamsFor(level, state.selections);
      const key = fetchKeyFor(state.requestId[level], params);
      if (fetchKeysRef.current[level] === key) return;

      fetchKeysRef.current[level] = key;
      startFetch(level, params);
    });
  }, [state, abortLevel, startFetch]);

  // Nothing outlives the component: every open request is cancelled on unmount.
  useEffect(
    () => () => {
      mountedRef.current = false;
      CASCADE_ORDER.forEach((level) => {
        const controller = controllersRef.current[level];
        controllersRef.current[level] = null;
        if (controller !== null) controller.abort();
      });
    },
    [],
  );

  /**
   * Records a selection for one level (Req 8.9).
   *
   * The reducer clears the descendants and invalidates their requests; the abort loop
   * here closes the sockets those invalidated requests are still holding.
   */
  const select = useCallback(
    (level, values) => {
      descendantsOf(level).forEach(abortLevel);
      dispatch({ type: "SELECT", level, values });
    },
    [abortLevel],
  );

  /** Clears every filter and reloads fiscal years, as the Reset Filters button does. */
  const reset = useCallback(() => {
    CASCADE_ORDER.forEach(abortLevel);
    // `RESET` empties the fiscal-year options too, so every key is forgotten and the
    // driving effect refills the one level that needs no selections.
    fetchKeysRef.current = levelRecord(() => null);
    dispatch({ type: "RESET" });
  }, [abortLevel]);

  /**
   * Re-requests one level's options — the retry path for a level showing an error.
   *
   * The request is started here rather than by clearing the key and waiting for the
   * driving effect: a retry changes no selection, so the reducer may well return the
   * identical state, no re-render happens, and the effect would never run again.
   */
  const refresh = useCallback(
    (level) => {
      if (!CASCADE_ORDER.includes(level)) return;

      const { selections, requestId } = stateRef.current;
      if (!hasSelections(selections, REQUIRED_SELECTIONS[level])) return;

      const params = requestParamsFor(level, selections);
      fetchKeysRef.current[level] = fetchKeyFor(requestId[level], params);
      startFetch(level, params);
    },
    [startFetch],
  );

  /**
   * All seven selections serialized under their backend parameter names (Req 8.8),
   * ready to spread into the data-panel fetchers.
   */
  const params = useMemo(
    () =>
      CASCADE_ORDER.reduce((record, level) => {
        record[PARAM_NAMES[level]] = serializeMultiSelect(state.selections[level]);
        return record;
      }, {}),
    [state.selections],
  );

  /** Stable identity for `params`, for use as a data-fetch effect dependency. */
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  /** True once fiscal year, period, and division are chosen — the data panels' floor. */
  const canFetch = useMemo(
    () => hasSelections(state.selections, DATA_PANEL_REQUIRED),
    [state.selections],
  );

  return useMemo(
    () => ({
      selections: state.selections,
      options: state.options,
      loading: state.loading,
      errors: state.errors,
      params,
      paramsKey,
      canFetch,
      select,
      reset,
      refresh,
    }),
    [
      state.selections,
      state.options,
      state.loading,
      state.errors,
      params,
      paramsKey,
      canFetch,
      select,
      reset,
      refresh,
    ],
  );
}

export default useSfeFilters;
